import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import {
  useDeleteWebhook,
  useSaveWebhook,
  useWebhookDeliveries,
  useWebhooks,
} from '../api/hooks';
import type { Webhook, WebhookDelivery } from '../api/types';
import { extractErrorMessage } from '../api/client';
import { DeliveryStatusTag } from '../utils/status';
import { formatDateTime } from '../utils/format';

const { Title } = Typography;

const EVENT_VALUES = [
  'job.completed',
  'job.failed',
  'job.cancelled',
  'provider.error',
  'worker.offline',
];
const EVENT_OPTIONS = EVENT_VALUES.map((e) => ({ value: e, label: e }));

/** Sinh secret ký HMAC ngẫu nhiên ở trình duyệt (whsec_ + 48 hex). */
function genSecret(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return 'whsec_' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const DELIVERY_FILTER = [
  { value: '', label: 'Tất cả' },
  { value: 'pending', label: 'Đang chờ' },
  { value: 'success', label: 'Thành công' },
  { value: 'failed', label: 'Thất bại' },
];

export default function WebhooksPage() {
  return (
    <div>
      <Title level={3}>Webhook</Title>
      <Tabs
        items={[
          { key: 'endpoints', label: 'Điểm nhận (Endpoints)', children: <Endpoints /> },
          { key: 'deliveries', label: 'Lịch sử gửi', children: <Deliveries /> },
        ]}
      />
    </div>
  );
}

function Endpoints() {
  const { message } = AntApp.useApp();
  const list = useWebhooks();
  const save = useSaveWebhook();
  const del = useDeleteWebhook();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const [form] = Form.useForm();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    // Mặc định: tích chọn TẤT CẢ sự kiện + secret HMAC tự sinh sẵn.
    form.setFieldsValue({ enabled: true, events: [...EVENT_VALUES], secret: genSecret() });
    setOpen(true);
  };

  const openEdit = (r: Webhook) => {
    setEditing(r);
    form.setFieldsValue({ ...r, secret: r.secret ?? '' });
    setOpen(true);
  };

  const regenerateSecret = () => form.setFieldsValue({ secret: genSecret() });

  const onSubmit = async () => {
    const v = await form.validateFields();
    try {
      await save.mutateAsync({ id: editing?.id, data: v });
      message.success(editing ? 'Đã cập nhật webhook' : 'Đã tạo webhook');
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    { title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
    {
      title: 'Sự kiện',
      dataIndex: 'events',
      key: 'events',
      render: (events: string[]) =>
        (events ?? []).map((e) => <Tag key={e}>{e}</Tag>),
    },
    {
      title: 'Bật',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="success">Bật</Tag> : <Tag>Tắt</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_: unknown, r: Webhook) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm
            title="Xóa webhook?"
            okText="Xóa"
            cancelText="Hủy"
            onConfirm={() => del.mutate(r.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm webhook
        </Button>
      </Space>
      <Table<Webhook>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
      />

      <Drawer
        title={editing ? 'Sửa webhook' : 'Thêm webhook'}
        width={520}
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
            <Button type="primary" loading={save.isPending} onClick={onSubmit}>
              Lưu
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Tên"
            rules={[{ required: true, message: 'Vui lòng nhập tên' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="url"
            label="URL"
            rules={[
              { required: true, message: 'Vui lòng nhập URL' },
              { type: 'url', message: 'URL không hợp lệ' },
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="secret"
            label="Webhook Secret (HMAC)"
            tooltip="Dùng ký HMAC-SHA256 (header X-Gateway-Signature) cho mọi request gửi tới endpoint này. Tự sinh sẵn — có thể tạo lại."
            extra="Để trống = dùng secret hệ thống. Lưu lại giá trị này để bên nhận xác thực chữ ký."
          >
            <Input
              readOnly
              addonAfter={
                <Tooltip title="Tạo secret mới">
                  <SyncOutlined onClick={regenerateSecret} style={{ cursor: 'pointer' }} />
                </Tooltip>
              }
              placeholder="whsec_…"
            />
          </Form.Item>
          <Form.Item name="events" label="Sự kiện">
            <Checkbox.Group options={EVENT_OPTIONS} />
          </Form.Item>
          <Form.Item name="enabled" label="Bật" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

function Deliveries() {
  const [status, setStatus] = useState('');
  const list = useWebhookDeliveries(status);

  const columns = [
    { title: 'Sự kiện', dataIndex: 'event', key: 'event' },
    { title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <DeliveryStatusTag status={s} />,
    },
    { title: 'Số lần thử', dataIndex: 'attempts', key: 'attempts', width: 100 },
    {
      title: 'Mã phản hồi',
      dataIndex: 'responseCode',
      key: 'responseCode',
      render: (v: number) => v ?? '—',
    },
    {
      title: 'Lỗi gần nhất',
      dataIndex: 'lastError',
      key: 'lastError',
      ellipsis: true,
      render: (v: string) =>
        v ? (
          <Tooltip title={v}>
            <span>{v}</span>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Gửi lúc',
      dataIndex: 'deliveredAt',
      key: 'deliveredAt',
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Select
          value={status}
          style={{ width: 180 }}
          options={DELIVERY_FILTER}
          onChange={setStatus}
        />
        <Button icon={<ReloadOutlined />} onClick={() => list.refetch()}>
          Làm mới
        </Button>
      </Space>
      <Table<WebhookDelivery>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data ?? []}
        columns={columns}
      />
    </div>
  );
}
